import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loading, EmptyState, Section8Badge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, BedDouble, Bath, MapPin, Wrench, Pencil, Trash2, FileDown, Loader2, Hourglass, Share2 } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/api";
import { AnnualReportButton } from "@/components/AnnualReportButton";
import { vacancyTone } from "@/components/VacancyPanel";
import { ListingDialog } from "@/components/listing/ListingDialog";
import { PropertyPhotoField } from "@/components/PropertyPhotoField";
import { uploadChunked } from "@/lib/upload";

function VacancyBox({ v, onSetRent }) {
  if (!v) return <div className="mt-4 rounded-xl bg-slate-50 border border-dashed border-slate-300 p-3 text-center text-xs text-slate-500">No active lease</div>;
  const since = new Date(v.since + "T12:00:00Z").toLocaleDateString([], { month: "short", day: "numeric" });
  return (
    <div className={`mt-4 rounded-xl border p-3 ${vacancyTone(v.days)}`} data-testid="vacancy-box">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-bold"><Hourglass className="h-4 w-4" /> Vacant {v.days} day{v.days === 1 ? "" : "s"}</span>
        <span className="text-[11px] opacity-80">since {since}</span>
      </div>
      {v.rent_lost != null ? (
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="opacity-80">Rent lost so far</span>
          <span className="font-bold" data-testid="vacancy-rent-lost">~{fmtMoney(v.rent_lost)} <span className="font-normal opacity-70">({fmtMoney(v.monthly_rent)}/mo {v.rent_source === "target" ? "target" : "last lease"})</span></span>
        </div>
      ) : (
        <button type="button" onClick={onSetRent} className="mt-2 text-xs font-semibold underline underline-offset-2 hover:opacity-80" data-testid="vacancy-set-rent">Set a target rent to estimate lost income</button>
      )}
    </div>
  );
}

function StatementDialog({ prop, onClose }) {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadFile(`/properties/${prop.id}/statement`, `statement-${prop.name.replace(/\W+/g, "-").toLowerCase()}-${period}.pdf`, { period });
      toast.success("Statement downloaded");
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!prop} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="statement-dialog">
        <DialogHeader><DialogTitle>Monthly statement · {prop?.name}</DialogTitle>
          <DialogDescription>A PDF with rent, HAP, late fees, payments received and balances for the month — handy for taxes and your accountant.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label>Month</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="statement-period" /></div>
        <DialogFooter><Button onClick={download} disabled={busy || !period} className="w-full gap-2" data-testid="statement-download">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { name: "", address: "", city: "", state: "", zip: "", property_type: "Single Family Home", bedrooms: 3, bathrooms: 2, image_url: "", description: "", target_rent: 0 };
const STOCK = [
  "https://images.unsplash.com/photo-1720065609938-ec0e33ffd9ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwcmVudGFsJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkxMzQ0NTE3fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1777106322601-578dc9213ace?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwcmVudGFsJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkxMzQ0NTE3fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1787419559815-e2a3623355bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwcmVudGFsJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkxMzQ0NTE3fDA&ixlib=rb-4.1.0&q=85",
];

function PropertyForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uploaded = !!initial?.cover_photo && form.image_url === initial.cover_photo.url;

  const save = async () => {
    if (!form.name || !form.address) return toast.error("Name and address are required");
    setSaving(true);
    try {
      const payload = { ...form, target_rent: Number(form.target_rent) || 0, image_url: form.image_url || STOCK[Math.floor(Math.random() * STOCK.length)] };
      const saved = await onSave(payload);
      if (file) {
        setProgress(1);
        const meta = await uploadChunked(file, setProgress);
        await api.post(`/properties/${saved.id}/photo`, meta);
      }
      onClose(true);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const removeUploaded = async () => {
    try { await api.delete(`/properties/${initial.id}/photo`); set("image_url", ""); toast.success("Uploaded photo removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Property name</Label>
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Maple Street House" data-testid="prop-name" />
      </div>
      <div className="space-y-1.5">
        <Label>Street address</Label>
        <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="412 Maple Street" data-testid="prop-address" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>State</Label><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>ZIP</Label><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>Type</Label><Input value={form.property_type} onChange={(e) => set("property_type", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Beds</Label><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", Number(e.target.value))} /></div>
        <div className="space-y-1.5"><Label>Baths</Label><Input type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", Number(e.target.value))} /></div>
      </div>
      <div className="space-y-1.5">
        <Label>Target rent / month</Label>
        <Input type="number" min={0} value={form.target_rent ?? 0} onChange={(e) => set("target_rent", e.target.value)} placeholder="1800" data-testid="prop-target-rent" />
        <p className="text-[11px] text-slate-400">Used to estimate rent lost while vacant.</p>
      </div>
      <PropertyPhotoField imageUrl={form.image_url || ""} onUrlChange={(v) => set("image_url", v)} file={file} onFileChange={setFile}
        uploaded={uploaded} onRemoveUploaded={removeUploaded} progress={progress} />
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="prop-save">{saving ? (progress !== null ? `Uploading ${progress}%` : "Saving…") : "Save property"}</Button>
      </DialogFooter>
    </div>
  );
}

export default function Properties() {
  const [props, setProps] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stmtFor, setStmtFor] = useState(null);
  const [listingFor, setListingFor] = useState(null);

  const load = () => api.get("/properties").then((r) => setProps(r.data));
  useEffect(() => { load(); }, []);

  const create = async (payload) => { const { data } = await api.post("/properties", payload); toast.success("Property added"); return data; };
  const update = async (payload) => { const { data } = await api.put(`/properties/${editing.id}`, payload); toast.success("Property updated"); return data; };
  const remove = async (id) => {
    try { await api.delete(`/properties/${id}`); toast.success("Property deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!props) return <Loading />;

  return (
    <div>
      <PageHeader title="Properties" subtitle="Every house in your rental portfolio."
        action={
          <div className="flex flex-wrap gap-2">
          <AnnualReportButton />
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button className="gap-2" data-testid="add-property-btn"><Plus className="h-4 w-4" /> Add property</Button></DialogTrigger>
            <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Add a property</DialogTitle></DialogHeader>
              <PropertyForm onSave={create} onClose={(saved) => { setAddOpen(false); if (saved) load(); }} />
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {props.length === 0 ? (
        <EmptyState icon={Building2} title="No properties yet" subtitle="Add your first rental house to get started." testid="props-empty"
          action={<Button onClick={() => setAddOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> Add property</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {props.map((p) => (
            <div key={p.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden card-hover" data-testid={`property-card-${p.id}`}>
              <div className="relative h-44 bg-slate-100">
                <img src={p.image_url || STOCK[0]} alt={p.name} className="h-full w-full object-cover" />
                <div className="absolute top-3 left-3 flex gap-1.5">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${p.occupied ? "bg-emerald-600 text-white" : "bg-white text-slate-700"}`}>
                    {p.occupied ? "Occupied" : "Vacant"}
                  </span>
                  {p.listing?.enabled && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 text-white px-2.5 py-1 text-xs font-semibold" data-testid={`listing-live-badge-${p.id}`}><Share2 className="h-3 w-3" /> Listed</span>}
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display font-bold text-slate-900">{p.name}</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{p.address}, {p.city} {p.state}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
                  <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{p.bedrooms} bd</span>
                  <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{p.bathrooms} ba</span>
                  {p.open_maintenance > 0 && <span className="flex items-center gap-1 text-rose-600"><Wrench className="h-3.5 w-3.5" />{p.open_maintenance}</span>}
                </div>
                {p.lease ? (
                  <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">{p.lease.tenant?.name}</span>
                      <Section8Badge section8={p.lease.section8} />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="text-slate-500">Monthly rent</span>
                      <span className="font-semibold text-slate-900">{fmtMoney(p.lease.total_rent)}</span>
                    </div>
                    {p.lease.section8 && (
                      <div className="flex items-center justify-between mt-1 text-xs">
                        <span className="text-amber-700">HAP {fmtMoney(p.lease.hap_amount)}</span>
                        <span className="text-indigo-700">Tenant {fmtMoney(p.lease.tenant_portion)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <VacancyBox v={p.vacancy} onSetRent={() => setEditing(p)} />
                )}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setEditing(p)} data-testid={`edit-property-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setStmtFor(p)} data-testid={`statement-property-${p.id}`}>
                    <FileDown className="h-3.5 w-3.5" /> Statement
                  </Button>
                  <Button variant="outline" size="sm" className={`flex-1 gap-1.5 ${p.listing?.enabled ? "border-indigo-200 text-indigo-700 hover:bg-indigo-50" : ""}`} onClick={() => setListingFor(p)} data-testid={`listing-property-${p.id}`}>
                    <Share2 className="h-3.5 w-3.5" /> Listing{p.listing?.new_applications > 0 && <span className="rounded-full bg-emerald-600 text-white text-[10px] font-bold px-1.5" data-testid={`listing-new-badge-${p.id}`}>{p.listing.new_applications}</span>}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-property-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This cannot be undone. End any active lease first.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => remove(p.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Edit property</DialogTitle></DialogHeader>
          {editing && <PropertyForm initial={editing} onSave={update} onClose={(saved) => { setEditing(null); if (saved) load(); }} />}
        </DialogContent>
      </Dialog>
      {stmtFor && <StatementDialog prop={stmtFor} onClose={() => setStmtFor(null)} />}
      <ListingDialog prop={listingFor} open={!!listingFor} onOpenChange={(o) => !o && setListingFor(null)} onChanged={load} />
    </div>
  );
}
